import { useDispatch } from "react-redux";
import Actions from "src/components/actionbar";
import { editCollection } from "src/redux/actions/collections";
import { DealRoomContext } from "../context";
import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import DefaultUserProfileImage from 'src/legacy/images/default_user_profile_background.png';
import { MenuState, ModuleEnum } from "../../types";
import styles from './styles.module.scss'
import { DealRoom, DealRoomBanner, DealRoomEditState, StakeHolderTypeEnum } from "src/types/dealRoom";
import { useTransition, animated, Spring } from "@react-spring/web";
import { useSelector } from "react-redux";
import _ from "lodash";
import { toast } from "react-toastify";
import { parseMediaMetadata } from "src/utils/media.service";
import { MediaFile } from "src/types/media";
import { invalidateMediaUpload, uploadMedia } from "src/redux/actions/media";
import cx from "classnames";
import useMeasure from "react-use-measure";
import { POSITIONS } from "src/constants/banner";
import { editDealRoom } from "src/redux/actions/dealRoom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import userRoleService from "src/utils/userRoles.service";
import { Carousel, Form, OverlayTrigger, Popover } from "react-bootstrap";
import { SmartCardTypes } from "src/utils/dealRoom.helper";
import Thumbnail from "src/components/thumbnail";
import Tooltip from "src/components/tooltip";
import { useDealRoomAdoption } from "src/redux/actions/adoption/dealRoom";
import { copyToClipboard } from "src/utils/util";

interface ViewMenuProps {
  show: boolean;
  showAddMeetingLink: () => void;
}

const ViewMenu: React.FC<ViewMenuProps> = ({ show, showAddMeetingLink }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { trackDealRoomEditClicked, trackDealRoomMeetingLinkClicked } = useDealRoomAdoption();

  const { dealRoom, id, onMenuChange, dealRoomAssets, handleShareClick, hasContents, handleAddAssetClick, activeAssetLayout } = React.useContext(DealRoomContext);
  const externalStakeholders = useSelector((state) => _.get(state, `dealRooms.dealRoom[${id}].stakeholders.people.[${StakeHolderTypeEnum.EXTERNAL}`, {}));
  const containMeetingLink = useSelector((state) => _.get(state, `dealRooms.dealRoom[${id}].item.meetingLink`,{}));

  const dealRoomCreatedBy = _.get(dealRoom, 'createdBy', {});
  const firstName = _.get(dealRoomCreatedBy, `firstName`, '');
  const lastName = _.get(dealRoomCreatedBy, `lastName`, '');
  const icon = _.get(dealRoomCreatedBy, `icon.thumbnail`, null);
  const dealRoomOwnerName = `${firstName} ${lastName}`;
  const dealRoomOwnerMail = _.get(dealRoomCreatedBy, `email`, '');
  const dealRoomOwnerTitle = _.get(dealRoomCreatedBy, `title`, '');
  const dealRoomOwnerMobilePhone = _.get(dealRoomCreatedBy, `mobilePhone`, '');
  const dealRoomOwnerWorkPhone = _.get(dealRoomCreatedBy, `workPhone`, '');
  const hasUnreadMessages = useSelector((state: any) => {
    const smartCards = _.get(state, `dealRooms.dealRoom["${dealRoom?.id ?? ''}"].smartCards.items`, []);
    const messageListCard = _.find(smartCards, { type: SmartCardTypes.MESSAGE_LIST });

    return _.get(messageListCard, 'unReadMessages', 0) > 0;
  });

  const allAssetsData = useSelector((state: any) => {
      return _.get(state, `dealRooms.dealRoom['${id}'].assets`);
  });
  
  const [dealRoomUrlCopied, setDealRoomUrlCopied] = useState<boolean>(false);

  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

 const pathName = useLocation().pathname;
 const { type } = useParams();
 const isExternalAssetsPage = pathName.includes('assets') && type === 'external';

  useEffect(() => {
    api.start();
  }, [show]);

  const handleEdit = () => {
    onMenuChange({
      primaryAction: 'edit'
    });
    dispatch(editDealRoom({
      dealRoom: dealRoom,
      assets: dealRoomAssets,
      allAssets: allAssetsData
    }));
  }

  const handleAssetViewerChange = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'asset-viewer'
    });
    dispatch(editDealRoom({
      dealRoom: dealRoom,
      assets: dealRoomAssets,
      allAssets: allAssetsData
    }));
  }

  return transitions((style, item) => item &&
    <animated.div style={style} className={cx(styles.viewMenu)}>
      <Actions.Container>
        {_.get(dealRoom, "canEdit", false) && <Actions.Row>
           <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={() => {
              handleEdit();
              trackDealRoomEditClicked();
            }}
          />
          {userRoleService.canCollaborateDealRoom() && <Actions.IconButton
            icon="paperflite-share"
            tooltip={hasContents ? "Share" : "Add assets to share"}
            onClick={handleShareClick}
            disabled={!hasContents}
          />}
          { _.get(dealRoom, "dealRoomUrl", false) &&
             <Actions.Button
              className={styles.clipboardCopy}
              width={dealRoomUrlCopied ? 3 : 1}
               icon="paperflite-link"
               title={dealRoomUrlCopied ? 'link copied!' : null}
               onClick={() => {
                      copyToClipboard(_.get(dealRoom, "dealRoomUrl", '')).then(res => {
                        if (res) { // copied
                          setDealRoomUrlCopied(true);
                          setTimeout(() => setDealRoomUrlCopied(false), 2000);
                        }
                      });
                    }}
               tooltip={hasContents && _.size(_.get(externalStakeholders,"items",[])) > 0  ? "Copy Link" : "Add assets/stakeholders to copy link"}
               disabled={!hasContents || _.size(_.get(externalStakeholders,"items",[])) === 0 }
            />}

          <Actions.Button
            width={containMeetingLink ? 4 : 3}
            icon={"paperflite-calendar"}
            title={ containMeetingLink ? "Update meeting link" : "Add meeting link"}
            onClick={() => { 
              trackDealRoomMeetingLinkClicked();
              handleEdit();
              showAddMeetingLink()
            }}
          />
          <Actions.Button
            width={3}
            icon={"paperflite-add"}
            title="Add assets"
            disabled={isExternalAssetsPage}
            onClick={handleAddAssetClick}
          />
        </Actions.Row>}

        <Actions.Row>
     
          {_.size(_.get(externalStakeholders,"items",[])) > 0 && <Actions.Button
            className={cx({
              [styles.showUnreadMessage]: hasUnreadMessages,
            })}
            width={4}
            icon={"paperflite-text-message"}
            title="Start a conversation"
            onClick={() => {
              navigate(`/deal-room/${dealRoom.id}/engage`);
            }}
          />}
          {_.get(dealRoom, "canEdit", false) && <Actions.IconButton
            icon="paperflite-reverse"
            tooltip="Change viewer"
            onClick={() => {
              handleAssetViewerChange();
            }}
          />}

          <Actions.Seek />

          <SharedByEditMenu dealRoom={dealRoom} />

        </Actions.Row>

      </Actions.Container>
    </animated.div>
  )
}

interface EditMenuProps {
  menuState: MenuState,
  onMenuChange: (value: MenuState) => void,
}

const EditMenu: React.FC<EditMenuProps> = ({
  menuState, onMenuChange,
}) => {
  const dealRoomContext = React.useContext(DealRoomContext);
  const updating = useSelector((state: any) => _.get(state, `${dealRoomContext.updateSelector}.updating`, false));
  const activeModule = _.get(dealRoomContext, "activeModule", "");
  const isActionPlanActive = activeModule === ModuleEnum.ACTION_PLAN;
  const handleSave = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'save'
    });
  }

  const handleCancel = () => {
    onMenuChange({
      ...menuState,
      primaryAction: 'view'
    });
  }

  return (
    <Reveal
      show={menuState.primaryAction === "edit"}
      className={styles.deleteConfirmation}
    >
      <div className={styles.message}>
        {isActionPlanActive
          ? "Your changes are saved automatically. Switch to view mode."
          : "Save your changes"}
      </div>
      <Button
        variant="primary"
        icon="paperflite-tick"
        text={
          isActionPlanActive
            ? updating
              ? "Exiting"
              : "Exit"
            : updating
              ? "Saving..."
              : "Save"
        }
        disabled={updating}
        onClick={handleSave}
      />
      {activeModule !== ModuleEnum.ACTION_PLAN && (
        <Button variant="secondary" text="Cancel" onClick={handleCancel} />
      )}
    </Reveal>
  );
};

interface MenubarProps {
  dealRoom: DealRoom;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: MenuState) => void;
}

const Menubar: React.FC<MenubarProps> = ({ dealRoom, menuState, onChange }) => {
  const dealRoomContext = useContext(DealRoomContext);

  const actionBarRef = React.useRef<HTMLDivElement>(null);

  const persistedDealRoom = useSelector((state: any) => {
    return _.get(state, `${dealRoomContext.itemSelector}`);
  });

  const [viewMenuRef, viewMenuBounds] = useMeasure();
  const [editMenuRef, editMenuBounds] = useMeasure();
  const [bannerMenuRef, bannerMenuBounds] = useMeasure();
  const [confirmationMenuRef, confirmationMenuBounds] = useMeasure();
  const [positionMenuRef, positionMenuBounds] = useMeasure();

  const showCrop = menuState.primaryAction === 'edit-banner' && (_.includes(['crop', 'apply-crop'], menuState.secondaryAction));
  const showFilter = menuState.primaryAction === 'edit-banner' && (_.includes(['filter'], menuState.secondaryAction));
  const showConfirmation = showCrop || showFilter;
  const showPosition = menuState.primaryAction === 'edit-banner' && (_.includes(['position'], menuState.secondaryAction));

  const [dimensions, setDimensions] = React.useState<{ width: number | string, height: number | string, top: number | string }>({
    width: 'auto',
    height: 0,
    top: 0
  });

  const handleMenuChange = (value) => {
    onChange({
      ...menuState,
      ...value
    });
  }

  const handleConfirmationClick = () => {
    if (showCrop) {
      handleMenuChange({
        ...menuState,
        secondaryAction: 'apply-crop'
      });


    } else if (showFilter) {
      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });

    }
  }

  const handleConfirmationCancel = () => {
    if (showCrop) {
      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });
    } else if (showFilter) {
      dealRoomContext.updateDealRoomProperty('banner', {
        ...dealRoom.banner,
        filter: persistedDealRoom.banner?.filter,
        overlayColor: persistedDealRoom.banner?.overlayColor
      });

      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });
    }
  }

  useEffect(() => {
    let width = bannerMenuBounds.width || 0;
    let height = bannerMenuBounds.height || 0;
    let top = -100;
    if (menuState.primaryAction === 'view') {
      width = viewMenuBounds.width || 0;
      height = viewMenuBounds.height || 0;
      top = -100;
    } else if (menuState.primaryAction === 'edit') {
      width = editMenuBounds.width || 0;
      height = editMenuBounds.height || 0;
      top = -100;
    } else if (menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction) {
      width = bannerMenuBounds.width || 0;
      height = bannerMenuBounds.height || 0;
      top = 0;
    } else if (showConfirmation) {
      width = confirmationMenuBounds.width || 0;
      height = confirmationMenuBounds.height || 0;
      top = 0;
    } else if (showPosition) {
      width = positionMenuBounds.width || 0;
      height = positionMenuBounds.height || 0;
      top = 0;
    }

    setDimensions({
      width: width + 20,
      height: height + 20,
      top: top
    });

  }, [menuState.primaryAction, viewMenuBounds, editMenuBounds, bannerMenuBounds, confirmationMenuBounds, positionMenuBounds, showConfirmation, showPosition]);

  return (
    <Spring to={{ width: dimensions.width, height: dimensions.height, top: dimensions.top }} config={{
      friction: 14,
      tension: 120,
    }}>
      {props => (
        <animated.div ref={actionBarRef} style={props} className={styles.actionBar}>

          <div className={cx(styles.menu, styles.bannerMenu, { [styles.show]: menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction })}>
            <div ref={bannerMenuRef} className={styles.inner}>
              <EditBannerMenu
                dealRoom={dealRoom}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </div>
          </div>

          <div className={cx(styles.menu, styles.confirmationMenu, {
            [styles.show]: showConfirmation
          })}>
            <div ref={confirmationMenuRef} className={styles.inner}>
              <ConfirmationMenu onConfirm={handleConfirmationClick} onCancel={handleConfirmationCancel} />
            </div>
          </div>

          <div className={cx(styles.menu, styles.positionMenu, {
            [styles.show]: showPosition
          })}>
            <div ref={positionMenuRef} className={styles.inner}>
              <BannerPositionMenu
                dealRoom={dealRoom}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </div>
          </div>

        </animated.div>
      )}
    </Spring>
  );
}

interface BannerPositionMenuProps {
  dealRoom: DealRoom;

  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ dealRoom, menuState, onMenuChange }) => {
  const dealRoomContext = useContext(DealRoomContext);
  const positionValue = _.get(dealRoom, 'banner.position');

  const persistedDealRoom = useSelector((state: any) => {
    return _.get(state, `${dealRoomContext.itemSelector}`);
  });

  const handlePositionChange = (position: string) => {
    dealRoomContext.updateDealRoomProperty('banner', {
      ...dealRoom.banner,
      position: position
    });
  }

  const handleConfirm = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleCancel = () => {
    dealRoomContext.updateDealRoomProperty('banner', {
      ...dealRoom.banner,
      position: persistedDealRoom.banner?.position
    });

    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  return (
    <Actions.Container>
      <Actions.Row className={styles.positionActionRow}>
        <Actions.Item className={styles.positions}>
          {POSITIONS.map((position) => {
            const active = position.value === positionValue;
            return (
              <div key={position.value}
                className={cx(styles.position, {
                  [styles.active]: active
                })}
                onClick={() => handlePositionChange(position.value)}
              />
            );
          })}
        </Actions.Item>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Save" onClick={handleConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={handleCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}

interface ConfirmationMenuProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationMenu: React.FC<ConfirmationMenuProps> = ({ onConfirm, onCancel }) => {
  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Done" onClick={onConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={onCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}


interface EditBannerMenuProps {
  dealRoom: DealRoom;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const EditBannerMenu: React.FC<EditBannerMenuProps> = ({ dealRoom, menuState, onMenuChange }) => {
  const dispatch = useDispatch();
  const dealRoomContext = useContext(DealRoomContext);

  const originalBanner = useSelector((state: any) => {
    return _.get(state, `${dealRoomContext.itemSelector}.banner`, null);
  });

  const uploading = useSelector((state: any) => {
    const background = _.get(state, `${dealRoomContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loading`, false);
    }

    return false;
  });

  const uploaded = useSelector((state: any) => {
    const background = _.get(state, `${dealRoomContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loaded`, false);
    }

    return false;
  });

  const uploadedBackground = useSelector((state: any) => {
    const background = _.get(state, `${dealRoomContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].item`);
    }

    return false;
  });

  const canCrop = useMemo(() => {
    return dealRoom?.banner?.background?.type === 'image';
  }, [dealRoom]);

  useEffect(() => {
    if (uploaded) {
      let banner = _.get(dealRoom, 'banner');
      const mediaId = _.get(banner, 'background.id');

      banner = {
        ...banner,
        background: uploadedBackground
      } as DealRoomBanner;

      dealRoomContext.updateDealRoomProperty('banner', banner);

      if (mediaId) {
        dispatch(invalidateMediaUpload(mediaId));
      }

      onMenuChange({
        ...menuState,
        primaryAction: 'edit'
      });
    }
  }, [uploaded, uploadedBackground, onMenuChange, menuState]);

  const handleBannerFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const backgroundMedia = await parseMediaMetadata(file);

    dealRoomContext.updateDealRoomProperty('banner', {
      ...dealRoom.banner,
      background: backgroundMedia,
      crop: null
    });

  }

  const handleSaveBanner = async () => {
    const banner = _.get(dealRoom, 'banner');
    const media = _.get(banner, 'background');
    const entityType = _.get(media, 'entityType');

    if (media && entityType === 'media-preview') {
      const mediaFile = media as MediaFile;
      const blob = await fetch(media!.full!).then(r => r.blob());
      const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

      dispatch(uploadMedia({
        id: media.id,
        media: {
          ...media,
          file: file
        } as MediaFile
      }));
    } else {
      onMenuChange({
        ...menuState,
        primaryAction: 'edit'
      });
    }
  }


  const handleCancel = () => {
    dealRoomContext.updateDealRoomProperty('banner', originalBanner);

    onMenuChange({
      ...menuState,
      primaryAction: 'edit'
    });
  }

  const handleCrop = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'crop'
    });
  }

  const handleFilter = () => {

    onMenuChange({
      ...menuState,
      secondaryAction: 'filter'
    });
  }

  const handlePosition = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'position'
    });
  }

  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.FileUpload width={5}
          icon="paperflite-upload"
          title="Upload from device"
          accept="image/*,video/*"
          disabled={uploading}
          onChange={handleBannerFileChange}
        />
        {canCrop &&
          <>
            <Actions.IconButton
              icon="paperflite-crop"
              tooltip="Crop"
              disabled={uploading}
              onClick={handleCrop}
            />
            <Actions.IconButton
              icon="paperflite-focus"
              tooltip="Position"
              disabled={uploading}
              onClick={handlePosition}
            />
          </>
        }

        {!canCrop &&

          <Actions.Button
            icon="paperflite-focus"
            width={2}
            title="Position"
            disabled={uploading}
            onClick={handlePosition}
          />
        }

      </Actions.Row>
      <Actions.Row>
        <Actions.Button width={2}
          icon="paperflite-filter-property"
          title="Filters"
          disabled={uploading}
          onClick={handleFilter}
        />
        <Actions.Button width={3}
          icon="paperflite-tick"
          title={uploading ? 'Saving...' : 'Save Changes'}
          className={styles.primaryAction}
          disabled={uploading}
          onClick={handleSaveBanner}
        />
        <Actions.Button width={2}
          icon="paperflite-close"
          title="Cancel"
          disabled={uploading}
          onClick={handleCancel}
        />
      </Actions.Row>
    </Actions.Container>
  );
}

interface AssetViewerSelectorProps {
  viewers: any[];
  selected: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (viewer: string) => void;
}

const AssetViewerSelector: React.FC<AssetViewerSelectorProps> = ({
  viewers,
  selected,
  onSelect,
}) => {

  const descriptionTransition = useTransition(selected, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <div className={styles.experiences}>
      <p>
        Select the viewer that best suits your content and audience for this
        deal room.
      </p>
      <Carousel
        activeIndex={viewers.findIndex((item) => item.id === selected)}
        indicators={false}
        controls={false}
      >
        {viewers.map((viewer) => (
          <Carousel.Item key={viewer.id}>
            <img src={viewer.icon} alt={viewer.name} />
          </Carousel.Item>
        ))}
      </Carousel>
      <div className={styles.types}>
        {viewers.map((item) => (
          <button
            key={item.id}
            className={cx(styles.type, {
              [styles.active]: item.id === selected,
            })}
            onClick={() => onSelect(item.id)}
          >
            <div className={styles.name}>{item.name}</div>
          </button>
        ))}
      </div>
      <div className={styles.description}>
        {descriptionTransition((style, item) => {
          const viewer = viewers.find((e) => e.id === item);
          if (!viewer) {
            return false;
          }

          return (
            <animated.div style={style} className={styles.inner}>
              {viewer.description}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

interface SharedByEditMenuProps {
  dealRoom: DealRoomEditState;
}

const SharedByEditMenu: React.FC<SharedByEditMenuProps> = ({ dealRoom }) => {
  const dealRoomContext = useContext(DealRoomContext);
  const { onMenuChange, dealRoomAssets, id } = dealRoomContext;
  const user = useSelector((state: any) => state.users?.me);
  const enabled = _.get(dealRoom, 'banner.sharedByEnabled');
  const dispatch = useDispatch();
  const allAssetsData = useSelector((state: any) => {
      return _.get(state, `dealRooms.dealRoom['${id}'].assets`);
  });
  
  const handleChange = () => {

    if(!_.get(dealRoom, "canEdit", false)){
      return;
    }

    onMenuChange({
      primaryAction: 'edit'
    });
    dispatch(editDealRoom({
      dealRoom: dealRoom,
      assets: dealRoomAssets,
      allAssets: allAssetsData
    }));
    dealRoomContext.updateDealRoomProperty('banner', {
      ...dealRoom.banner,
      sharedByEnabled: !enabled
    });
  }

  return (
    <Actions.Item className={cx(styles.sharedByMenu, { [styles.disabled]: !enabled })}>
        <OverlayTrigger placement="bottom" overlay={
          <Popover className={cx(styles.sharedByTooltip)} placement={'bottom'}>
            <Popover.Body>
              <div className={styles.name}>
                {`${user.firstName || ''} ${user.lastName || ''} `}
              </div>
              <div className={styles.email}>
                {user.email || ''}
              </div>
              {user.title &&
                <div className={styles.title}>
                  {user.title}
                </div>
              }

              {user.mobilePhone &&
                <div className={styles.mobile}>
                  {user.mobilePhone}
                </div>
              }

              {user.workPhone &&
                <div className={styles.workPhone}>
                  {user.workPhone}
                </div>
              }
            </Popover.Body>
          </Popover>
        }
          container={document.body}
          trigger={['hover', 'focus']}>
          <span>
            <Actions.Item className={styles.userIcon}>
              <Thumbnail media={user?.icon} width={100} height={100} />
            </Actions.Item>
          </span>
        </OverlayTrigger>
      <Tooltip placement="auto" content={ enabled ? "Hide Shared By" : "Show Shared By"}>
        <Form.Check type="switch" className={cx(styles.switch,{
          [styles.disabled]: !_.get(dealRoom, "canEdit", false)
        })} checked={enabled} onChange={handleChange} disabled={!_.get(dealRoom, "canEdit", false)} />
      </Tooltip>
    </Actions.Item>
  );
}

export { ViewMenu, EditMenu, Menubar, AssetViewerSelector };